import { Alert, Button, Form, Radio } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { pageHeight, pageWidth } from '../../../constants/gridConfig';
import { MASKS_TYPE } from '../../../constants/constants';
import { useStore } from '../../../store/store';
import { useOverlay, leftShownStatus } from '../../../contexts/OverlayContext';
import {
  PAGE_BACKGROUND_ID,
  PAGE_BACKGROUND_MODES,
  findPageBackground,
  pageBackgroundMode,
} from '../../../utils/pageBackground';
import PageBackgroundFrame from '../../common/PageBackgroundFrame';

// Page d'aperçu, réduite pour tenir dans le panneau.
const PREVIEW_SCALE = 0.3;

// Onglet « Décor de page » : traits, formes et images par-dessus le contenu de
// chaque page du PDF. L'édition se fait dans une page à part, encadrée de
// l'en-tête et du pied (cf. Middle), qu'ouvre le bouton.
export const TabsPageBackground = ({ closeConfiguration }) => {
  const landscape = useStore(({ landscape }) => landscape);
  const elementsCount = useStore(
    ({ masks }) => findPageBackground(masks)?.boxes?.length ?? 0
  );
  // Une section reprend par défaut le décor du document où elle est insérée
  // (comme son en-tête et son pied, cf. masksTab), ou impose le sien.
  const isSectionTemplate = useStore(
    ({ currentTemplate }) => currentTemplate?.pattern === 'section'
  );
  const mode = useStore(({ configuration }) => pageBackgroundMode(configuration));
  const setConfiguration = useStore(({ setConfiguration }) => setConfiguration);
  const addMissingMask = useStore(({ addMissingMask }) => addMissingMask);
  const setMaskEditing = useStore(({ setMaskEditing }) => setMaskEditing);
  const { showLeftPanel } = useOverlay();

  // Le décor se peuple en glissant formes/images depuis la palette : sans ce
  // changement d'onglet, l'utilisateur atterrit en mode édition avec le rail
  // encore sur « Paramètres » et ne sait pas où trouver la palette.
  const editPageBackground = () => {
    addMissingMask(MASKS_TYPE.BACKGROUND);
    setMaskEditing([PAGE_BACKGROUND_ID]);
    showLeftPanel(leftShownStatus.ELEMENTS);
    closeConfiguration();
  };

  const hasOwn = mode === PAGE_BACKGROUND_MODES.OWN;

  const width = landscape ? pageHeight : pageWidth;
  const height = landscape ? pageWidth : pageHeight;

  return (
    <>
      <Alert
        type='info'
        showIcon
        style={{ marginBottom: 16 }}
        message='Visible uniquement dans le PDF'
        description='Le décor n’apparaît pas dans le document en cours d’édition : il se voit dans l’aperçu PDF et dans le PDF généré.'
      />
      <p style={{ marginBottom: 16 }}>
        Traits, formes et images posés sur toute la page du PDF, en-tête et
        pied de page compris, sous le contenu. Les pages pleine page
        (couverture, séparateurs, PDF importés en pleine page) n’en ont pas.
      </p>
      {isSectionTemplate && (
        <Form.Item label='Sur les pages de cette section'>
          <Radio.Group
            value={mode}
            onChange={(e) =>
              setConfiguration({ pageBackgroundMode: e.target.value })
            }
          >
            <Radio.Button value={PAGE_BACKGROUND_MODES.INHERIT}>
              Décor du document
            </Radio.Button>
            <Radio.Button value={PAGE_BACKGROUND_MODES.OWN}>
              Décor propre
            </Radio.Button>
          </Radio.Group>
          <div style={{ marginTop: 8, color: 'rgba(0, 0, 0, 0.45)' }}>
            {hasOwn
              ? elementsCount === 0
                ? 'Décor propre vide : les pages de cette section n’auront aucun décor, même si le document en a un.'
                : 'Le décor ci-dessous remplace celui du document sur les pages de cette section.'
              : 'Les pages de cette section prennent le décor du document où elle est insérée.'}
          </div>
        </Form.Item>
      )}
      {(!isSectionTemplate || hasOwn) && (
        <Form.Item label='Décor de page'>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              type='primary'
              icon={
                <FontAwesomeIcon icon={icon({ name: 'pen', style: 'light' })} />
              }
              onClick={editPageBackground}
            >
              Éditer le décor de page
            </Button>
            <span>
              {elementsCount === 0
                ? 'Aucun élément'
                : `${elementsCount} élément${elementsCount > 1 ? 's' : ''}`}
            </span>
          </div>
        </Form.Item>
      )}
      {/* En mode « décor du document », il n'y a rien à montrer ici : le décor
          qui s'appliquera est celui du document d'accueil, inconnu de la
          section. */}
      {(!isSectionTemplate || hasOwn) && (
      <Form.Item label='Aperçu'>
        <div
          style={{
            width: width * PREVIEW_SCALE,
            height: height * PREVIEW_SCALE,
            overflow: 'hidden',
            backgroundColor: 'white',
            border: '1px solid #d9d9d9',
          }}
        >
          <div
            style={{
              width,
              height,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transform: `scale(${PREVIEW_SCALE})`,
              transformOrigin: 'top left',
            }}
          >
            <PageBackgroundFrame />
          </div>
        </div>
      </Form.Item>
      )}
    </>
  );
};
